import React, { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { useParams, useSearchParams } from 'react-router-dom';
import { Spinner } from '@xometry/xometry_loft';

import axios from '../../utils/axios-wrapper';
import BuyerRegistrationPanel from './BuyerRegistrationPanel';
import { API_ORIGIN, WWW_ORIGIN } from '../../config';
import { GetLoggedInUserQuery } from '../../schema/generated/schema';
import quotesApi from '../../services/quotes';

import { buyerRegistrationStyles } from './BuyerRegistration.styled';
import { convertToQuoteSubdomain } from '../../utils/helpers';

const decodeTemporaryPasswordKey = async (key) => {
  return axios.get(
    `${API_ORIGIN}/v2/users/decode-temporary-password-key/${key}`
  );
};

const statusMessage = window.encodeURIComponent(
  'Your account has already been registered.'
);

const errorMessage = window.encodeURIComponent(
  'Oops. It looks like you tried to complete registration using a temporary link that expired. You can try again by entering your email address below. A new link will be sent to your email. Please note that temporary link expire after 24 hours.'
);

const redirectToForgotPassword = () => {
  window.location.assign(
    `${WWW_ORIGIN}/account/ulp-forgot-password?error=${errorMessage}`
  );
};

/**
 * The complete registration page prepopulates the email address field
 * and allows a user to fill in their account details.
 *
 * The user's email address is determined by either their session or
 * by a temporary password key. If both are present, then the session
 * is preferred because it is more secure.
 */
const CompleteRegistration = () => {
  const [sessionEmailAddress, setSessionEmailAddress] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [isDecryptingKey, setIsDecryptingKey] = useState(false);
  const [decryptedEmail, setDecryptedEmail] = useState('');

  const [searchParams] = useSearchParams();
  const destination = searchParams.get('destination');
  const temporaryKey = searchParams.get('key');

  const { loading, error, data } = useQuery(GetLoggedInUserQuery);

  // useParams case to handle redirects for legacy temp key URLs; remove once the migration is complete
  const { temporaryKey: legacyTempKey } = useParams();

  const redirectToQuote = () => {
    let redirectUrl = `${WWW_ORIGIN}/quoting/home`;
    if (destination) {
      const quoteDestination = convertToQuoteSubdomain(
        decodeURIComponent(destination) || '/quote'
      );
      // statusMessage is hardcoded currently; the ternary anticipates future modularity
      redirectUrl = statusMessage
        ? `${quoteDestination}?status=${statusMessage}`
        : quoteDestination;
    }
    window.location.assign(redirectUrl);
  };

  useEffect(() => {
    const loggedInUser = data ? data.me : null;
    if (loggedInUser) {
      if (loggedInUser.registered) {
        redirectToQuote();
      }
      setSessionEmailAddress(loggedInUser.emailAddress);
    }
  }, [data]);

  // Temporary redirect to handle legacy temp key URLs; remove once the migration is complete
  const handleLegacyKeyRedirect = () => {
    // Preserve existing query params during legacy redirects
    const legacyUrlParams = new URLSearchParams(window.location.search);
    const redirectUrlParams = new URLSearchParams({ key: legacyTempKey });

    if (legacyUrlParams.has('destination')) {
      redirectUrlParams.set('destination', legacyUrlParams.get('destination'));
    }

    window.location.assign(
      `${WWW_ORIGIN}/account/buyer/complete-registration?${redirectUrlParams.toString()}`
    );
  };

  const handleDecodeTemporaryPassword = async (key) => {
    try {
      setIsDecryptingKey(true);

      const res = await decodeTemporaryPasswordKey(key);
      const userInfo = res.data;

      if (userInfo.is_registered) {
        redirectToQuote();
      }

      setDecryptedEmail(userInfo.email_address);
      setHasPassword(userInfo.has_password);
    } catch (error) {
      console.warn('Error decoding temporary password key:', error);
      redirectToForgotPassword();
    } finally {
      setIsDecryptingKey(false);
    }
  };

  useEffect(() => {
    if (window.analytics) {
      window.analytics.page('Complete Registration', {
        category: 'Instant Quote Funnel',
        source: 'MigratedGetCompleteRegistration',
      });
    }

    if (legacyTempKey) {
      handleLegacyKeyRedirect();
    }

    if (temporaryKey) {
      (async () => {
        await handleDecodeTemporaryPassword(temporaryKey);
      })();
    }
  }, []);

  if (loading || isDecryptingKey) {
    return <Spinner scale="full-screen" />;
  }

  if (error) {
    console.error(error);
  }

  return (
    <div css={buyerRegistrationStyles.widgetBody}>
      <BuyerRegistrationPanel
        prefilledEmail={sessionEmailAddress || decryptedEmail}
        currentUserHasPassword={hasPassword}
        destination={destination}
        handleRegister={(formData) =>
          quotesApi.submitCompleteRegistration(formData)
        }
        mbsy={searchParams.get('mbsy')}
        handleRedirect={(url) => window.location.assign(url)}
        temporaryPasswordKey={temporaryKey}
      />
    </div>
  );
};

export default CompleteRegistration;
